import {
  Alert,
  Box,
  Button,
  Link,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@locus-taxy/encore-mui';
import { useTranslation } from '@locus-taxy/encore-i18n-toolkit';
import { useAuth0 } from '../auth';
import LocusLogo from '../assets/locus-logo.png';
import LoginBackground from '../assets/login-background.svg';
import { useEffect, useState, useCallback, useMemo } from 'react';
import { STORAGE_KEYS } from '../utils/storageKeys';
import { Storage } from '../utils/storage';
import { useClientId, hasSubdomainClientId } from '../hooks/useClientId';
import { useClientMetadata, type ClientMetadataResponse } from '../api';
import {
  ApiClient,
  endpointRegistry,
  type RequestConfig,
} from '@locus-taxy/encore-ui-network';
import { getApiBaseUrl } from '../config';
import type { AxiosRequestConfig } from 'axios';
import {
  buildFormButtonTestId,
  buildFormTestId,
  getTestId,
} from '@locus-taxy/encore-utils';
import { TestIdAction } from '../utils/testIdUtils';

async function fetchOrganizationId(
  clientId: string,
  hasSubdomain: boolean,
  metadataData: ClientMetadataResponse | null | undefined
): Promise<string | undefined> {
  if (!clientId) {
    return undefined;
  }

  if (hasSubdomain) {
    return metadataData?.data?.organizationId;
  }

  const metadataBaseUrl = getApiBaseUrl(clientId);
  const client = ApiClient.getInstance(metadataBaseUrl);
  const path = endpointRegistry.getEndpoint('client.metadata', {
    clientId,
  });
  const result = await client.get<ClientMetadataResponse>(path, {
    requireAuth: false,
  } as RequestConfig & AxiosRequestConfig);
  return result?.data?.organizationId;
}

function saveClientIdToStorage(clientId: string): void {
  try {
    if (clientId) {
      Storage.set(STORAGE_KEYS.clientId, clientId);
    }
  } catch {
    // ignore storage errors
  }
}

function useAuth0ErrorMessage() {
  const { t } = useTranslation('fallback');
  const { error: auth0Error } = useAuth0();

  return useMemo(() => {
    if (!auth0Error) {
      return null;
    }
    const msg = auth0Error.message ?? '';
    if (msg.includes('SSO_MIGRATED')) {
      return t('loginPage.errors.SSO_MIGRATED');
    }
    if (msg.includes('user is blocked')) {
      return t('loginPage.errors.USER_BLOCKED');
    }
    return t('loginPage.errors.UNKNOWN');
  }, [auth0Error, t]);
}

export default function LoginPage() {
  const { t } = useTranslation('fallback');
  const { loginWithRedirect } = useAuth0();
  const [inputClientId, setInputClientId] = useState('');

  const hostname = window.location.hostname;
  const hasSubdomain = hasSubdomainClientId(hostname);
  const clientId = useClientId();
  const effectiveClientId = clientId || inputClientId || '';

  const auth0ErrorMessage = useAuth0ErrorMessage();

  const metadataClientId = hasSubdomain ? clientId : effectiveClientId;
  const {
    isLoading,
    isError,
    data: metadataData,
  } = useClientMetadata(metadataClientId || null, {
    enabled: hasSubdomain ? Boolean(clientId) : false,
  });

  useEffect(() => {
    // Only load from storage for legacy URLs (new structure doesn't need manual input)
    if (!hasSubdomain) {
      try {
        const existing = Storage.get(STORAGE_KEYS.clientId) ?? '';
        setInputClientId(existing);
      } catch {
        // ignore storage errors
      }
    }
  }, [hasSubdomain]);

  const handleLogin = useCallback(async () => {
    // For new URL structure, block login if metadata fetch failed
    if (hasSubdomain && isError) {
      return;
    }

    // Clear stale auth callback params so the SDK treats this as a fresh login
    const url = new URL(window.location.href);
    if (url.searchParams.has('error') || url.searchParams.has('code')) {
      url.searchParams.delete('error');
      url.searchParams.delete('error_description');
      url.searchParams.delete('code');
      url.searchParams.delete('state');
      window.history.replaceState({}, '', url.pathname + url.search);
    }

    // For legacy URLs, save clientId to storage if provided
    if (!hasSubdomain) {
      saveClientIdToStorage(inputClientId);
    }

    const returnTo = url.pathname + (url.search ? url.search : '');

    let organization: string | undefined;
    try {
      organization = await fetchOrganizationId(
        effectiveClientId,
        hasSubdomain,
        metadataData
      );
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error fetching client metadata:', error);
      // For new URL structure, block login on error
      if (hasSubdomain) {
        return;
      }
      // For legacy URLs, proceed without organization
    }

    const appState = { returnTo };
    if (organization) {
      loginWithRedirect({
        appState,
        authorizationParams: {
          organization,
          l_client_id: effectiveClientId || inputClientId,
          prompt: 'login',
        },
      });
    } else {
      loginWithRedirect({
        appState,
        authorizationParams: {
          prompt: 'login',
        },
      });
    }
  }, [
    hasSubdomain,
    isError,
    inputClientId,
    effectiveClientId,
    loginWithRedirect,
    metadataData,
  ]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100vh',
        bgcolor: 'grey.50',
      }}
    >
      <Box
        component="header"
        sx={{
          p: 2,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <img
          src={LocusLogo}
          alt="Locus Logo"
          style={{ height: 32 }}
          data-testid={getTestId({
            action: TestIdAction.VIEW,
            object: 'image',
            entity: 'locus-logo',
            page: 'login-page',
          })}
        />
        <Link
          href="https://locus.sh/documents/MaraLabsTermsOfUse.pdf"
          target="_blank"
          rel="noopener noreferrer"
          variant="body2"
          color="primary"
        >
          {t('loginPage.termsOfUse')}
        </Link>
      </Box>
      <Box
        sx={{
          flexGrow: 1,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          p: { xs: 2, md: 4 },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            alignItems: 'center',
            gap: { xs: 4, md: 8 },
            maxWidth: 'lg',
          }}
        >
          <Box sx={{ flex: 1, textAlign: { xs: 'center', md: 'left' } }}>
            <Typography variant="h2">{t('loginPage.welcome')}</Typography>
          </Box>
          <Box sx={{ flexShrink: 0, width: { xs: '100%', sm: 350 } }}>
            <Paper
              elevation={3}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                p: 3,
                borderRadius: 2,
              }}
            >
              {auth0ErrorMessage && (
                <Alert severity="warning" sx={{ width: '100%' }}>
                  {auth0ErrorMessage}
                </Alert>
              )}
              <Stack
                direction="column"
                gap={4}
                width="100%"
                alignItems="center"
                justifyContent="center"
                p={4}
              >
                <Typography variant="h6">
                  {t('loginPage.clickLogin')}
                </Typography>
                {!hasSubdomain && (
                  <TextField
                    label="Client ID"
                    placeholder={t('loginPage.enterClientId')}
                    fullWidth
                    value={inputClientId}
                    onChange={e => setInputClientId(e.target.value)}
                    data-testid={buildFormTestId({
                      fieldType: 'text',
                      fieldName: 'client-id',
                      entity: 'login-page',
                      formAction: 'submit',
                    })}
                  />
                )}
                <Button
                  variant="contained"
                  fullWidth
                  size="large"
                  disabled={isLoading || (hasSubdomain && isError)}
                  loading={isLoading}
                  onClick={handleLogin}
                  data-testid={buildFormButtonTestId({
                    kind: 'submit-form',
                    entity: 'login-page',
                    formAction: 'submit',
                  })}
                >
                  {t('loginPage.login')}
                </Button>
              </Stack>
            </Paper>
          </Box>
        </Box>
      </Box>
      <Box
        sx={{
          backgroundImage: `url(${LoginBackground})`,
          backgroundRepeat: 'no-repeat',
          backgroundSize: 'cover',
          backgroundPosition: 'center bottom',
          height: { xs: 200, sm: 300, md: 400 },
        }}
      />
      <Box
        component="footer"
        sx={{
          p: 2,
          textAlign: 'center',
          bgcolor: 'grey.900',
          color: 'grey.100',
        }}
      >
        <Typography variant="body2">{t('loginPage.helpAndSupport')}</Typography>
      </Box>
    </Box>
  );
}
